// Succession & talent pipeline (Phase 6) — HR view. Critical positions with
// ranked successors + readiness + risk coverage.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Trash2, GitBranch, UserPlus } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const RISKS = ['Low', 'Medium', 'High'];
const READINESS = ['Ready now', '1-2 yrs', '3-5 yrs', 'Long term'];
const riskTone = (r) => (r === 'High' ? 'error' : r === 'Medium' ? 'warning' : 'neutral');
const readyTone = (r) => (r === 'Ready now' ? 'success' : r === '1-2 yrs' ? 'info' : 'neutral');

export default function Succession({ showToast }) {
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [addTo, setAddTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.succession().then(setData).catch(() => setData({ plans: [], stats: { total: 0, atRisk: 0, covered: 0 } }));
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);

  const create = async () => {
    if (!form.positionTitle.trim() || busy) return; setBusy(true);
    try { await api.createSuccessionPlan(form); showToast?.('Plan created'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const removePlan = async (p) => { try { await api.deleteSuccessionPlan(p.id); load(); } catch (e) { showToast?.(e.message); } };
  const setRisk = async (p, riskLevel) => { try { await api.updateSuccessionPlan(p.id, { riskLevel }); load(); } catch (e) { showToast?.(e.message); } };
  const addSuccessor = async () => {
    if (!addTo.employeeId || busy) return; setBusy(true);
    try { await api.addSuccessor(addTo.planId, { employeeId: addTo.employeeId, readiness: addTo.readiness }); setAddTo(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const removeSuccessor = async (planId, sid) => { try { await api.removeSuccessor(planId, sid); load(); } catch (e) { showToast?.(e.message); } };

  if (!data) return <CenterSpinner />;
  const s = data.stats;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Succession" sub="Critical positions & talent pipeline" />
        <PrimaryBtn onClick={() => setForm({ positionTitle: '', incumbentId: '', riskLevel: 'Medium', notes: '' })}><Plus size={14} style={{ marginRight: 6 }} /> New plan</PrimaryBtn>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[['Critical positions', s.total], ['Covered (ready now)', s.covered], ['At risk', s.atRisk]].map(([l, v]) => (
          <Card key={l} style={{ padding: 14 }}><div style={{ fontSize: 22, fontWeight: 700, color: AG.text }}>{v}</div><div style={{ fontSize: 12, color: AG.n500 }}>{l}</div></Card>
        ))}
      </div>

      {data.plans.length === 0 ? <Card><EmptyState icon={GitBranch} title="No succession plans" desc="Identify a critical position and add successors." /></Card> : (
        <div style={{ display: 'grid', gap: 10 }}>
          {data.plans.map((p) => (
            <Card key={p.id} style={{ padding: 16, borderLeft: p.atRisk ? `3px solid ${AG.error}` : undefined }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, color: AG.text }}>{p.positionTitle} {p.atRisk && <Pill tone="error">At risk</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>Incumbent: {p.incumbentName || 'Vacant'} · {p.coverage.successors} successor(s)</div>
                </div>
                <div className="flex items-center gap-1">
                  <select value={p.riskLevel} onChange={(e) => setRisk(p, e.target.value)} className="ag-input" style={{ width: 100, padding: '4px 8px' }}>{RISKS.map((r) => <option key={r}>{r}</option>)}</select>
                  <button onClick={() => removePlan(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                {p.successors.map((su) => (
                  <div key={su.id} className="flex items-center justify-between" style={{ border: `1px solid ${AG.n200}`, borderRadius: 8, padding: '6px 10px' }}>
                    <span style={{ fontSize: 13.5, color: AG.text }}>{su.name} <span style={{ color: AG.n400 }}>· {su.role}</span></span>
                    <span className="flex items-center gap-2"><Pill tone={readyTone(su.readiness)}>{su.readiness}</Pill><button onClick={() => removeSuccessor(p.id, su.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 2 }}><Trash2 size={13} /></button></span>
                  </div>
                ))}
                <OutlineBtn onClick={() => setAddTo({ planId: p.id, employeeId: '', readiness: '1-2 yrs' })}><UserPlus size={14} style={{ marginRight: 4 }} /> Add successor</OutlineBtn>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New succession plan</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Critical position" value={form.positionTitle} onChange={(e) => setForm({ ...form, positionTitle: e.target.value })} sx={{ mb: 1.5 }} />
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Incumbent (optional)</div>
              <select value={form.incumbentId} onChange={(e) => setForm({ ...form, incumbentId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}><option value="">— Vacant / none —</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Risk</div>
              <select value={form.riskLevel} onChange={(e) => setForm({ ...form, riskLevel: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{RISKS.map((r) => <option key={r}>{r}</option>)}</select>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={create} disabled={busy}>Create</PrimaryBtn></DialogActions>
      </Dialog>

      <Dialog open={!!addTo} onClose={() => !busy && setAddTo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add successor</DialogTitle>
        <DialogContent>
          {addTo && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Employee</div>
              <select value={addTo.employeeId} onChange={(e) => setAddTo({ ...addTo, employeeId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}><option value="">Select…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}</select>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Readiness</div>
              <select value={addTo.readiness} onChange={(e) => setAddTo({ ...addTo, readiness: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{READINESS.map((r) => <option key={r}>{r}</option>)}</select>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setAddTo(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={addSuccessor} disabled={busy}>Add</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
